'use client';

import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { SlideOver } from '@/components/admin/SlideOver';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { AccountField } from '@/components/platform/AccountField';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import {
  ACCOUNT_EMAIL_MAX,
  ACCOUNT_NAME_MAX,
  ACCOUNT_PASSWORD_MAX,
  accountError,
  accountFieldError,
  agenciesOpenForAccounts,
  filterAgencyDirectory,
  leaveIfSignedOut,
  parseAgencyDirectory,
  parsePlatformAccount,
  parseStaffInvitation,
  upsertAgencyMember,
  type StaffInvitationRecord,
  type AgencyDirectoryEntry,
  type PlatformAccount,
} from '@/lib/platform/accounts';
import { isStaffRole, type StaffRole } from '@/lib/roles';

export function AgencyStaffPage() {
  const [rows, setRows] = useState<AgencyDirectoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<{ agency: AgencyDirectoryEntry; member: PlatformAccount } | null>(null);
  const [activeTarget, setActiveTarget] = useState<{ agency: AgencyDirectoryEntry; member: PlatformAccount } | null>(null);
  const [activePending, setActivePending] = useState(false);
  const [activeError, setActiveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/platform/staff')
      .then((payload) => {
        if (cancelled) return;
        setRows(parseAgencyDirectory(payload));
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || leaveIfSignedOut(err)) return;
        setRows(null);
        setError(accountError(err, 'Could not load the staff directory.'));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const visible = useMemo(() => (rows ? filterAgencyDirectory(rows, query) : []), [rows, query]);
  const openAgencies = rows ? agenciesOpenForAccounts(rows) : [];

  function storeMember(slug: string, member: PlatformAccount, mode: 'create' | 'replace') {
    setRows((current) => (current ? upsertAgencyMember(current, slug, member, mode) : current));
  }

  async function changeActive() {
    if (!activeTarget) return;
    setActivePending(true);
    setActiveError(null);
    try {
      const updated = parsePlatformAccount(
        await api<unknown>(`/api/platform/staff/${activeTarget.agency.slug}/${activeTarget.member.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ active: !activeTarget.member.active }),
        }),
      );
      storeMember(activeTarget.agency.slug, updated, 'replace');
      setActiveTarget(null);
      notify(updated.active ? 'Account restored.' : 'Account suspended.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setActiveError(accountError(err, 'Could not update this account.'));
    } finally {
      setActivePending(false);
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <label className="block min-w-0 flex-1 text-xs font-medium text-muted">
          Search
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Agency, slug, or email"
            className="mt-1 w-full max-w-md rounded-xl border border-line bg-card px-3 py-2 text-sm text-ink outline-none focus:border-brand"
          />
        </label>
        <button
          type="button"
          disabled={openAgencies.length === 0}
          title={openAgencies.length === 0 ? 'Every agency database is unavailable.' : undefined}
          onClick={() => setCreating(true)}
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50"
        >
          Create account
        </button>
      </div>
      <p className="text-sm text-muted">Each account stays in that agency’s database. Owner and front desk cannot open this console.</p>

      {error && (
        <div className="rounded-2xl border border-danger/40 bg-card px-4 py-6">
          <p className="text-sm text-danger">{error}</p>
          <button type="button" onClick={() => setReloadKey((current) => current + 1)} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
            Try again
          </button>
        </div>
      )}
      {rows === null && !error && <DirectorySkeleton />}
      {rows?.length === 0 && <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No agencies are registered yet.</p>}
      {rows && rows.length > 0 && visible.length === 0 && (
        <p className="rounded-2xl border border-line bg-card px-6 py-10 text-center text-sm text-muted">No accounts match that search.</p>
      )}
      <div className="space-y-4">
        {visible.map((agency) => (
          <AgencyGroup
            key={agency.slug}
            agency={agency}
            onEdit={(member) => setEditing({ agency, member })}
            onActive={(member) => {
              setActiveError(null);
              setActiveTarget({ agency, member });
            }}
            onRevoked={(id) => {
              setRows((current) => current?.map((row) => (
                row.slug === agency.slug
                  ? { ...row, invitations: row.invitations.filter((invite) => invite.id !== id) }
                  : row
              )) ?? current);
            }}
          />
        ))}
      </div>

      {creating && openAgencies.length > 0 && (
        <CreateStaff
          agencies={openAgencies}
          onClose={() => setCreating(false)}
          onCreated={(slug, invite) => {
            setRows((current) => current?.map((agency) => (
              agency.slug === slug
                ? { ...agency, invitations: [invite, ...agency.invitations.filter((row) => row.email !== invite.email)] }
                : agency
            )) ?? current);
            setCreating(false);
            notify('Invitation sent.');
          }}
        />
      )}
      {editing && (
        <EditStaff
          agency={editing.agency}
          member={editing.member}
          onClose={() => setEditing(null)}
          onSaved={(member) => {
            storeMember(editing.agency.slug, member, 'replace');
            setEditing(null);
            notify('Account updated.');
          }}
        />
      )}
      <ConfirmDialog
        open={activeTarget !== null}
        title={activeTarget?.member.active ? 'Suspend this staff account?' : 'Restore this staff account?'}
        description={
          activeTarget?.member.active
            ? `${activeTarget.member.name} will not be able to sign in at ${activeTarget.agency.name}. The agency must keep one active owner.`
            : `${activeTarget?.member.name ?? 'This account'} will be able to sign in at ${activeTarget?.agency.name ?? 'this agency'} again.`
        }
        confirmLabel={activeTarget?.member.active ? 'Suspend' : 'Restore'}
        pending={activePending}
        error={activeError}
        onCancel={() => {
          if (activePending) return;
          setActiveTarget(null);
          setActiveError(null);
        }}
        onConfirm={() => void changeActive()}
      />
    </section>
  );
}

function AgencyGroup({
  agency,
  onEdit,
  onActive,
  onRevoked,
}: {
  agency: AgencyDirectoryEntry;
  onEdit: (member: PlatformAccount) => void;
  onActive: (member: PlatformAccount) => void;
  onRevoked: (id: string) => void;
}) {
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokePending, setRevokePending] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const revokeTarget = agency.invitations.find((invite) => invite.id === revokeId);

  async function revoke() {
    if (!revokeId) return;
    setRevokePending(true);
    setRevokeError(null);
    try {
      await api(`/api/platform/staff/${agency.slug}/invitations/${revokeId}`, { method: 'DELETE' });
      onRevoked(revokeId);
      setRevokeId(null);
      notify('Invitation revoked.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setRevokeError(accountError(err, 'Could not revoke this invitation.'));
    } finally {
      setRevokePending(false);
    }
  }

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-card">
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-ink">{agency.name}</h2>
          <p className="truncate text-xs text-muted">{agency.slug}</p>
        </div>
        <AgencyStatus status={agency.status} />
      </header>
      {agency.error ? (
        <div className="space-y-1 px-4 py-4">
          <p className="text-xs font-semibold text-danger">{agency.error}</p>
          <p className="text-xs text-muted">New accounts are unavailable until this database can be opened.</p>
        </div>
      ) : agency.staff.length === 0 && agency.invitations.length === 0 ? (
        <p className="px-4 py-4 text-xs text-muted">No staff accounts for this agency yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {agency.staff.map((member) => (
            <li key={member.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{member.name}</p>
                <p className="truncate text-xs text-muted">{member.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted">{roleLabel(member.role)}</span>
                <AccountStatus active={member.active} />
                <button type="button" onClick={() => onEdit(member)} className="rounded-full border border-line px-3 py-1 text-xs font-semibold text-ink">
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onActive(member)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${member.active ? 'text-danger' : 'text-success'}`}
                >
                  {member.active ? 'Suspend' : 'Restore'}
                </button>
              </div>
            </li>
          ))}
          {agency.invitations.map((invite) => (
            <li key={invite.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{invite.name}</p>
                <p className="truncate text-xs text-muted">{invite.email} · Invitation pending</p>
              </div>
              <button type="button" onClick={() => setRevokeId(invite.id)} className="rounded-full px-3 py-1 text-xs font-semibold text-danger">
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke invitation"
        description={revokeTarget ? `Revoke the invitation for ${revokeTarget.email}.` : 'Revoke this invitation.'}
        confirmLabel="Revoke"
        error={revokeError}
        pending={revokePending}
        onCancel={() => { if (!revokePending) setRevokeId(null); }}
        onConfirm={() => void revoke()}
      />
    </article>
  );
}

function CreateStaff({
  agencies,
  onClose,
  onCreated,
}: {
  agencies: AgencyDirectoryEntry[];
  onClose: () => void;
  onCreated: (slug: string, invite: StaffInvitationRecord) => void;
}) {
  const formId = useId();
  const [slug, setSlug] = useState(agencies[0]?.slug ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('front_desk');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!agencies.some((agency) => agency.slug === slug)) {
      setError('Choose an agency whose database is open.');
      return;
    }
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    const invalid = accountFieldError({ name, email, password: '', passwordRequired: false });
    if (invalid || firstName.trim() === '' || lastName.trim() === '') {
      setError(invalid ?? 'Enter a first name and a last name.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const created = parseStaffInvitation(
        await api<unknown>(`/api/platform/staff/${slug}`, {
          method: 'POST',
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            name,
            email: email.trim(),
            role,
          }),
        }),
      );
      onCreated(slug, created);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not send the invitation.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver open title="Invite agency staff" onClose={onClose} footer={<SaveButton form={formId} pending={pending} label="Send invitation" />}>
      <form id={formId} onSubmit={(event) => void submit(event)} className="space-y-4">
        <AgencySelect agencies={agencies} slug={slug} onChange={setSlug} />
        <AccountField label="First name" value={firstName} onChange={setFirstName} autoComplete="given-name" maxLength={80} />
        <AccountField label="Last name" value={lastName} onChange={setLastName} autoComplete="family-name" maxLength={80} />
        <AccountField label="Email" value={email} onChange={setEmail} type="email" autoComplete="off" maxLength={ACCOUNT_EMAIL_MAX} />
        <RoleSelect role={role} onChange={setRole} />
        <p className="text-xs text-muted">They set a password from the invitation link. It expires in 7 days.</p>
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}

function EditStaff({
  agency,
  member,
  onClose,
  onSaved,
}: {
  agency: AgencyDirectoryEntry;
  member: PlatformAccount;
  onClose: () => void;
  onSaved: (member: PlatformAccount) => void;
}) {
  const formId = useId();
  const [name, setName] = useState(member.name);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<StaffRole>(isStaffRole(member.role) ? member.role : 'front_desk');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const invalid = accountFieldError({ name, email: member.email, password, passwordRequired: false });
    if (invalid) {
      setError(invalid);
      return;
    }
    setPending(true);
    setError(null);
    const body: { name: string; role: StaffRole; password?: string } = { name: name.trim(), role };
    if (password !== '') body.password = password;
    try {
      const updated = parsePlatformAccount(
        await api<unknown>(`/api/platform/staff/${agency.slug}/${member.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      );
      onSaved(updated);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not update the account.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver open title={`Edit ${member.name}`} onClose={onClose} footer={<SaveButton form={formId} pending={pending} label="Save changes" />}>
      <form id={formId} onSubmit={(event) => void submit(event)} className="space-y-4">
        <p className="text-xs text-muted">
          {member.email} · {agency.name}
        </p>
        <AccountField label="Name" value={name} onChange={setName} autoComplete="name" maxLength={ACCOUNT_NAME_MAX} />
        <RoleSelect role={role} onChange={setRole} />
        <AccountField
          label="New password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
          required={false}
          maxLength={ACCOUNT_PASSWORD_MAX}
          hint="Leave blank to keep the current password."
        />
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}

function AgencySelect({
  agencies,
  slug,
  onChange,
}: {
  agencies: AgencyDirectoryEntry[];
  slug: string;
  onChange: (slug: string) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="block text-xs font-medium text-muted">
      Agency
      <select
        id={id}
        value={slug}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand"
      >
        {agencies.map((agency) => (
          <option key={agency.slug} value={agency.slug}>
            {agency.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function RoleSelect({ role, onChange }: { role: StaffRole; onChange: (role: StaffRole) => void }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block text-xs font-medium text-muted">
      Role
      <select
        id={id}
        value={role}
        onChange={(event) => {
          if (isStaffRole(event.target.value)) onChange(event.target.value);
        }}
        className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand"
      >
        <option value="front_desk">Front desk</option>
        <option value="super_admin">Owner</option>
      </select>
    </label>
  );
}

function SaveButton({ form, pending, label }: { form: string; pending: boolean; label: string }) {
  return (
    <button type="submit" form={form} disabled={pending} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">
      {pending ? 'Saving…' : label}
    </button>
  );
}

function AgencyStatus({ status }: { status: string }) {
  const tone = status === 'active'
    ? 'border-success/30 bg-success/10 text-success'
    : status === 'paused'
      ? 'border-warning/30 bg-warning/10 text-warning'
      : 'border-line bg-shell text-muted';
  const label = status.length === 0 ? 'Unknown' : `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>{label}</span>;
}

function AccountStatus({ active }: { active: boolean }) {
  return <span className={`text-xs font-semibold ${active ? 'text-success' : 'text-danger'}`}>{active ? 'Active' : 'Suspended'}</span>;
}

function roleLabel(role: string): string {
  if (role === 'super_admin') return 'Owner';
  if (role === 'front_desk') return 'Front desk';
  return role;
}

function DirectorySkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <p className="sr-only">Loading the staff directory</p>
      {['one', 'two'].map((key) => (
        <div key={key} className="h-28 animate-pulse rounded-2xl border border-line bg-card" />
      ))}
    </div>
  );
}
